<template>
    <b-container v-if="authenticated" fluid class="dashboard-page">
        <b-row class="page-title align-items-center mb-3">
            <b-col>
                <h3 class="mb-1">Dashboard</h3>
                <p v-if="user" class="text-muted mb-0">Welcome back, {{ user.name }}.</p>
            </b-col>
            <b-col cols="auto" v-if="canViewDashboard">
                <div class="d-flex align-items-center flex-wrap dashboard-range-controls">
                    <b-dropdown size="sm" variant="outline-secondary" right>
                        <template #button-content>
                            <i class="fas fa-th-large mr-1"></i> Widgets
                        </template>
                        <b-dropdown-header>Show / hide widgets</b-dropdown-header>
                        <b-dropdown-form @click.stop>
                            <b-form-checkbox
                                v-for="widget in availableWidgetMetas"
                                :key="'toggle-' + widget.key"
                                class="mb-1"
                                :checked="isWidgetVisible(widget.key)"
                                @change="setWidgetVisible(widget.key, $event)"
                            >
                                {{ widget.label }}
                            </b-form-checkbox>
                        </b-dropdown-form>
                        <b-dropdown-divider></b-dropdown-divider>
                        <b-dropdown-item-button @click="resetLayout">
                            <i class="fas fa-undo mr-1"></i> Reset layout
                        </b-dropdown-item-button>
                    </b-dropdown>
                    <div v-if="canSelectSalesRep" class="dashboard-sales-rep-select">
                        <treeselect
                            v-model="salesRepId"
                            :options="salesRepOptions"
                            :clearable="true"
                            :searchable="true"
                            :disabled="loading"
                            placeholder="Sales Rep"
                            @input="onSalesRepChange"
                        />
                    </div>
                    <b-form-datepicker
                        id="dashboard-date-from"
                        v-model="dateFrom"
                        size="sm"
                        class="dashboard-date-picker"
                        placeholder="From"
                        today-button
                        close-button
                        reset-button
                        :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                        @input="onDateRangeChange"
                    />
                    <b-form-datepicker
                        id="dashboard-date-to"
                        v-model="dateTo"
                        size="sm"
                        class="dashboard-date-picker"
                        placeholder="To"
                        today-button
                        close-button
                        reset-button
                        :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                        @input="onDateRangeChange"
                    />
                    <b-button
                        size="sm"
                        variant="outline-secondary"
                        :disabled="!hasCustomDateRange"
                        @click="clearDateRange"
                    >
                        Clear
                    </b-button>
                    <b-button-group size="sm">
                        <b-button
                            v-for="option in periodOptions"
                            :key="option.value"
                            :variant="period === option.value ? 'primary' : 'outline-primary'"
                            @click="setPeriod(option.value)"
                        >
                            {{ option.label }}
                        </b-button>
                    </b-button-group>
                </div>
            </b-col>
        </b-row>

        <template v-if="canViewDashboard">
            <b-alert v-if="error" show variant="danger" dismissible @dismissed="error = ''">
                {{ error }}
            </b-alert>

            <b-row v-if="loading && !summary" class="mb-4">
                <b-col class="text-center py-5">
                    <b-spinner type="grow"></b-spinner>
                    <div class="mt-2 text-muted">Loading dashboard...</div>
                </b-col>
            </b-row>

            <template v-else-if="summary">
                <b-row>
                    <template v-for="(entry, index) in renderEntries">
                        <b-col
                            v-if="entry.type === 'group'"
                            :key="'group-' + entry.key + '-' + index"
                            cols="12"
                            class="mb-2 mt-2"
                        >
                            <h5 class="dashboard-section-title mb-0">{{ entry.label }}</h5>
                        </b-col>
                        <b-col
                            v-else
                            :key="'widget-' + entry.widget.key"
                            :cols="entry.meta.col.cols"
                            :sm="entry.meta.col.sm"
                            :lg="entry.meta.col.lg"
                            :xl="entry.meta.col.xl"
                            :class="['mb-3', entry.meta.col.className]"
                        >
                            <dashboard-widget
                                :widget-key="entry.widget.key"
                                :title="entry.meta.label"
                                :collapsed="parseInt(entry.widget.collapsed) === 1"
                                @toggle-collapse="toggleCollapse(entry.widget.key)"
                                @hide="hideWidget(entry.widget.key)"
                                @drop-on="onWidgetDrop"
                            >
                                <stat-card
                                    v-if="entry.widget.key === 'kpi_sales_today'"
                                    title="Sales Today"
                                    :value="summary.kpis.sales_today.value"
                                    format="currency"
                                    icon="fas fa-dollar-sign"
                                    :change-pct="summary.kpis.sales_today.change_pct"
                                    :loading="loading"
                                    :to="{ name: 'SalesOrders' }"
                                    embedded
                                />
                                <stat-card
                                    v-else-if="entry.widget.key === 'kpi_open_orders'"
                                    title="Open Orders"
                                    :value="summary.kpis.open_orders.total"
                                    :subtitle="openOrdersSubtitle"
                                    icon="fas fa-shopping-cart"
                                    :loading="loading"
                                    :to="{ name: 'SalesOrders' }"
                                    embedded
                                />
                                <stat-card
                                    v-else-if="entry.widget.key === 'kpi_unpaid_ar'"
                                    title="Unpaid AR"
                                    :value="summary.kpis.unpaid_ar.total"
                                    :subtitle="summary.kpis.unpaid_ar.count + ' orders'"
                                    format="currency"
                                    icon="fas fa-file-invoice-dollar"
                                    :loading="loading"
                                    :to="{ name: 'SalesOrders' }"
                                    embedded
                                />
                                <stat-card
                                    v-else-if="entry.widget.key === 'kpi_inventory_value'"
                                    title="Inventory Value"
                                    :value="summary.kpis.inventory_value"
                                    format="compact-currency"
                                    icon="fas fa-boxes"
                                    :loading="loading"
                                    :to="{ name: 'Products' }"
                                    embedded
                                />
                                <stat-card
                                    v-else-if="entry.widget.key === 'kpi_low_stock'"
                                    title="Low Stock"
                                    :value="summary.kpis.low_stock_count"
                                    subtitle="SKUs need attention"
                                    icon="fas fa-exclamation-triangle"
                                    :loading="loading"
                                    :to="{ name: 'Products' }"
                                    embedded
                                />
                                <stat-card
                                    v-else-if="entry.widget.key === 'kpi_open_pos'"
                                    title="Open POs"
                                    :value="summary.kpis.open_pos.count"
                                    :subtitle="'$' + numberWithCommas(summary.kpis.open_pos.value) + ' value'"
                                    icon="fas fa-truck-loading"
                                    :loading="loading"
                                    :to="{ name: 'Bills' }"
                                    embedded
                                />
                                <stat-card
                                    v-else-if="entry.widget.key === 'kpi_payments_mtd'"
                                    title="Payments MTD"
                                    :value="summary.kpis.payments_mtd"
                                    format="currency"
                                    icon="fas fa-money-check-alt"
                                    :loading="loading"
                                    :to="{ name: 'Payments' }"
                                    embedded
                                />
                                <stat-card
                                    v-else-if="entry.widget.key === 'kpi_total_ar'"
                                    title="Total AR"
                                    :value="summary.kpis.total_ar"
                                    format="currency"
                                    icon="fas fa-users"
                                    :loading="loading"
                                    :to="{ name: 'Customers' }"
                                    embedded
                                />
                                <stat-card
                                    v-else-if="entry.widget.key === 'kpi_open_picks'"
                                    title="Open Picks"
                                    :value="summary.kpis.open_picks"
                                    :subtitle="summary.kpis.active_batch_picks + ' active batches'"
                                    icon="fas fa-dolly"
                                    :loading="loading"
                                    :to="{ name: 'BatchPick' }"
                                    embedded
                                />
                                <stat-card
                                    v-else-if="entry.widget.key === 'kpi_pending_signups'"
                                    title="Pending Signups"
                                    :value="summary.kpis.pending_signups"
                                    subtitle="Awaiting review"
                                    icon="fas fa-user-plus"
                                    :loading="loading"
                                    :to="{ name: 'CustomerSignUpReview' }"
                                    embedded
                                />
                                <sales-ranking-card
                                    v-else-if="entry.widget.key === 'sales_last_week_rankings'"
                                    title="Last Week"
                                    :subtitle="lastWeekSubtitle"
                                    :total="summary.sales.last_week.total"
                                    :rankings="summary.sales.last_week.rankings"
                                    :loading="loading"
                                    :row-link-builder="lastWeekRankingLinkBuilder"
                                    embedded
                                />
                                <sales-ranking-card
                                    v-else-if="entry.widget.key === 'sales_last_month_rankings'"
                                    title="Last Month"
                                    :subtitle="lastMonthSubtitle"
                                    :total="summary.sales.last_month.total"
                                    :rankings="summary.sales.last_month.rankings"
                                    :loading="loading"
                                    :row-link-builder="lastMonthRankingLinkBuilder"
                                    embedded
                                />
                                <sales-ranking-card
                                    v-else-if="entry.widget.key === 'sales_closed_rankings'"
                                    title="Closed Invoices"
                                    :total="summary.sales.closed_invoices.total"
                                    :rankings="summary.sales.closed_invoices.rankings"
                                    :loading="loading"
                                    :row-link-builder="closedRankingLinkBuilder"
                                    embedded
                                />
                                <sales-ranking-card
                                    v-else-if="entry.widget.key === 'sales_unpaid_rankings'"
                                    title="Unpaid Invoices"
                                    :total="summary.sales.unpaid_invoices.total"
                                    :rankings="summary.sales.unpaid_invoices.rankings"
                                    :loading="loading"
                                    :row-link-builder="unpaidRankingLinkBuilder"
                                    embedded
                                />
                                <sales-ranking-card
                                    v-else-if="entry.widget.key === 'sales_aging_rankings'"
                                    title="Aging A/R 30+ Days"
                                    :total="summary.sales.aging_ar.total"
                                    :rankings="summary.sales.aging_ar.rankings"
                                    :loading="loading"
                                    :row-link-builder="agingRankingLinkBuilder"
                                    embedded
                                />
                                <sales-rep-pie-chart
                                    v-else-if="entry.widget.key === 'sales_closed_pie'"
                                    title="Closed Invoices"
                                    :data="summary.charts.closed_invoices"
                                    :loading="loading"
                                    embedded
                                />
                                <sales-rep-pie-chart
                                    v-else-if="entry.widget.key === 'sales_unpaid_pie'"
                                    title="Unpaid Invoices"
                                    :data="summary.charts.unpaid_invoices"
                                    :loading="loading"
                                    embedded
                                />
                                <sales-rep-pie-chart
                                    v-else-if="entry.widget.key === 'sales_aging_pie'"
                                    title="Aging A/R 30+ Days"
                                    :data="summary.charts.aging_ar"
                                    :loading="loading"
                                    embedded
                                />

                                <top-revenue-chart
                                    v-else-if="entry.widget.key === 'sales_top_products'"
                                    title="Top Products (after discount)"
                                    :data="summary.charts.top_products"
                                    label-key="sku"
                                    empty-text="No product sales for this period."
                                    color="#007bff"
                                    :loading="loading"
                                    embedded
                                />
                                <top-revenue-chart
                                    v-else-if="entry.widget.key === 'sales_top_customers'"
                                    title="Top Customers"
                                    :data="summary.charts.top_customers"
                                    label-key="customer_name"
                                    empty-text="No customer sales for this period."
                                    color="#28a745"
                                    :loading="loading"
                                    embedded
                                />
                                <velocity-list-card
                                    v-else-if="entry.widget.key === 'velocity_order_growth'"
                                    title="Customer Order Growth"
                                    :subtitle="'Trailing ' + velocityWindowDays + ' days vs prior ' + velocityWindowDays"
                                    :items="summary.velocity.order_growth"
                                    mode="growth"
                                    :loading="loading"
                                    embedded
                                />
                                <velocity-list-card
                                    v-else-if="entry.widget.key === 'velocity_lapse_ratio'"
                                    title="Lapse Ratio"
                                    :subtitle="'Days since last / avg gap · active in last ' + (velocityWindowDays * 2) + ' days · centered on 1.0'"
                                    :items="summary.velocity.lapse_ratio"
                                    mode="lapse"
                                    :loading="loading"
                                    scrollable
                                    :center-on="1"
                                    center-field="lapse_ratio"
                                    embedded
                                />
                                <velocity-list-card
                                    v-else-if="entry.widget.key === 'velocity_sku_breadth'"
                                    title="SKU Breadth"
                                    :subtitle="'Distinct parent products, trailing ' + velocityWindowDays + ' days'"
                                    :items="summary.velocity.sku_breadth"
                                    mode="breadth"
                                    :loading="loading"
                                    embedded
                                >
                                    <template #value="{ item }">
                                        {{ item.current }} SKUs
                                        <span>{{ breadthArrow(item.delta) }}</span>
                                        {{ formatDelta(item.delta) }}
                                        <span class="text-muted font-weight-normal">(was {{ item.previous }})</span>
                                    </template>
                                </velocity-list-card>
                            </dashboard-widget>
                        </b-col>
                    </template>
                </b-row>
            </template>
        </template>
    </b-container>
</template>

<script>
import { mapGetters } from 'vuex'
import Treeselect from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'
import StatCard from '../components/dashboard/StatCard.vue'
import SalesRankingCard from '../components/dashboard/SalesRankingCard.vue'
import SalesRepPieChart from '../components/dashboard/SalesRepPieChart.vue'
import TopRevenueChart from '../components/dashboard/TopRevenueChart.vue'
import VelocityListCard from '../components/dashboard/VelocityListCard.vue'
import DashboardWidget from '../components/dashboard/DashboardWidget.vue'
import {
    DASHBOARD_LAYOUT_AREA,
    DASHBOARD_GROUP_LABELS,
    DEFAULT_DASHBOARD_WIDGETS,
    createDefaultLayout,
    mergeDashboardLayout,
    moveWidget,
    getWidgetMeta,
} from '../utils/dashboardLayout'

export default {
    name: 'Home',
    components: {
        Treeselect,
        StatCard,
        SalesRankingCard,
        SalesRepPieChart,
        TopRevenueChart,
        VelocityListCard,
        DashboardWidget,
    },
    data() {
        return {
            loading: false,
            error: '',
            period: 'mtd',
            dateFrom: '',
            dateTo: '',
            salesRepId: null,
            salesRepOptions: [],
            viewSelfOnly: false,
            summary: null,
            layout: createDefaultLayout(),
            layoutLoaded: false,
            saveLayoutTimer: null,
            periodOptions: [
                { label: 'MTD', value: 'mtd' },
                { label: 'YTD', value: 'ytd' },
            ],
        }
    },
    computed: {
        ...mapGetters({
            authenticated: 'auth/authenticated',
            user: 'auth/user',
        }),
        canViewDashboard() {
            return this.$can('view-dashboard') || this.$can('view-all')
        },
        canSelectSalesRep() {
            return this.$can('view-all')
        },
        canViewSalesOrders() {
            return this.$can('view-sales-orders') || this.$can('view-all')
        },
        canViewProducts() {
            return this.$can('view-products') || this.$can('view-all')
        },
        canViewAr() {
            return this.$can('view-payments') || this.$can('view-sales-orders') || this.$can('view-all')
        },
        canViewBills() {
            return this.$can('view-bills') || this.$can('view-all')
        },
        canViewPayments() {
            return this.$can('view-payments') || this.$can('view-all')
        },
        canViewCustomers() {
            return this.$can('view-customers') || this.$can('view-all')
        },
        canViewBatchPick() {
            return this.$can('view-batch-pick') || this.$can('view-all')
        },
        canViewCustomerReview() {
            return this.$can('view-customer-review') || this.$can('view-all')
        },
        permissionMap() {
            return {
                salesOrders: this.canViewSalesOrders,
                products: this.canViewProducts,
                ar: this.canViewAr,
                bills: this.canViewBills,
                payments: this.canViewPayments,
                customers: this.canViewCustomers,
                batchPick: this.canViewBatchPick,
                customerReview: this.canViewCustomerReview,
            }
        },
        allowedWidgetKeys() {
            const keys = new Set()
            DEFAULT_DASHBOARD_WIDGETS.forEach((widget) => {
                if (!widget.permission || this.permissionMap[widget.permission]) {
                    keys.add(widget.key)
                }
            })
            return keys
        },
        availableWidgetMetas() {
            return DEFAULT_DASHBOARD_WIDGETS.filter((widget) => this.allowedWidgetKeys.has(widget.key))
        },
        renderEntries() {
            const entries = []
            let lastGroup = null

            this.layout.forEach((widget) => {
                if (parseInt(widget.visible) !== 1 || !this.allowedWidgetKeys.has(widget.key)) {
                    return
                }

                const meta = getWidgetMeta(widget.key)
                if (!meta) {
                    return
                }

                if (meta.group !== lastGroup) {
                    entries.push({
                        type: 'group',
                        key: meta.group,
                        label: DASHBOARD_GROUP_LABELS[meta.group] || meta.group,
                    })
                    lastGroup = meta.group
                }

                entries.push({
                    type: 'widget',
                    widget,
                    meta,
                })
            })

            return entries
        },
        hasCustomDateRange() {
            return Boolean(this.dateFrom || this.dateTo)
        },
        openOrdersSubtitle() {
            if (!this.summary || !this.summary.kpis || !this.summary.kpis.open_orders) {
                return ''
            }

            const open = this.summary.kpis.open_orders
            return open.open + ' open · ' + open.pending + ' pending · ' + open.confirmed + ' confirmed · ' + open.dispatched + ' dispatched'
        },
        velocityWindowDays() {
            if (this.summary && this.summary.velocity && this.summary.velocity.window_days) {
                return this.summary.velocity.window_days
            }

            return 90
        },
        lastWeekSubtitle() {
            return this.formatRankingRange(this.summary && this.summary.sales && this.summary.sales.last_week, 'Sun–Sat')
        },
        lastMonthSubtitle() {
            return this.formatRankingRange(this.summary && this.summary.sales && this.summary.sales.last_month, '')
        },
        lastWeekRankingLinkBuilder() {
            if (!this.canViewSalesOrders) {
                return null
            }
            return (row) => this.buildSalesRankingLink('last_week', row)
        },
        lastMonthRankingLinkBuilder() {
            if (!this.canViewSalesOrders) {
                return null
            }
            return (row) => this.buildSalesRankingLink('last_month', row)
        },
        closedRankingLinkBuilder() {
            if (!this.canViewSalesOrders) {
                return null
            }
            return (row) => this.buildSalesRankingLink('closed', row)
        },
        unpaidRankingLinkBuilder() {
            if (!this.canViewSalesOrders) {
                return null
            }
            return (row) => this.buildSalesRankingLink('unpaid', row)
        },
        agingRankingLinkBuilder() {
            if (!this.canViewSalesOrders) {
                return null
            }
            return (row) => this.buildSalesRankingLink('aging', row)
        },
    },
    mounted() {
        if (this.authenticated && this.canViewDashboard) {
            this.loadLayout()
            this.loadSummary()
        }
    },
    beforeDestroy() {
        if (this.saveLayoutTimer) {
            clearTimeout(this.saveLayoutTimer)
        }
    },
    methods: {
        setPeriod(period) {
            if (this.period === period && !this.dateFrom && !this.dateTo) {
                return
            }

            this.period = period
            this.dateFrom = ''
            this.dateTo = ''
            this.loadSummary()
        },
        onDateRangeChange() {
            if (this.dateFrom && this.dateTo) {
                if (this.dateFrom > this.dateTo) {
                    this.error = 'From date must be on or before To date.'
                    return
                }

                this.period = 'custom'
                this.loadSummary()
                return
            }

            if (this.period === 'custom') {
                this.clearDateRange()
            }
        },
        clearDateRange() {
            if (!this.hasCustomDateRange) {
                return
            }

            this.dateFrom = ''
            this.dateTo = ''
            this.period = 'mtd'
            this.loadSummary()
        },
        onSalesRepChange() {
            this.loadSummary()
        },
        breadthArrow(delta) {
            if (delta > 0) return '▲'
            if (delta < 0) return '▼'
            return '■'
        },
        formatDelta(delta) {
            if (delta > 0) return '+' + delta
            return String(delta)
        },
        isWidgetVisible(key) {
            const item = this.layout.find((widget) => widget.key === key)
            return item ? parseInt(item.visible) === 1 : true
        },
        setWidgetVisible(key, visible) {
            const index = this.layout.findIndex((widget) => widget.key === key)
            if (index < 0) {
                return
            }

            this.$set(this.layout, index, {
                ...this.layout[index],
                visible: visible ? 1 : 0,
            })
            this.queueSaveLayout()
        },
        hideWidget(key) {
            this.setWidgetVisible(key, false)
        },
        toggleCollapse(key) {
            const index = this.layout.findIndex((widget) => widget.key === key)
            if (index < 0) {
                return
            }

            const collapsed = parseInt(this.layout[index].collapsed) === 1 ? 0 : 1
            this.$set(this.layout, index, {
                ...this.layout[index],
                collapsed,
            })
            this.queueSaveLayout()
        },
        onWidgetDrop({ fromKey, toKey }) {
            this.layout = moveWidget(this.layout, fromKey, toKey)
            this.queueSaveLayout()
        },
        resetLayout() {
            this.layout = createDefaultLayout(this.allowedWidgetKeys)
            this.queueSaveLayout(true)
        },
        loadLayout() {
            axios.get('api/remembertable/getcolsaves/' + DASHBOARD_LAYOUT_AREA)
                .then((response) => {
                    if (response.data.status === 'success' && response.data.colSaves) {
                        this.layout = mergeDashboardLayout(
                            response.data.colSaves.column_array,
                            this.allowedWidgetKeys
                        )
                    } else {
                        this.layout = createDefaultLayout(this.allowedWidgetKeys)
                    }
                })
                .catch(() => {
                    this.layout = createDefaultLayout(this.allowedWidgetKeys)
                })
                .finally(() => {
                    this.layoutLoaded = true
                })
        },
        queueSaveLayout(immediate = false) {
            if (this.saveLayoutTimer) {
                clearTimeout(this.saveLayoutTimer)
            }

            if (immediate) {
                this.saveLayout()
                return
            }

            this.saveLayoutTimer = setTimeout(() => {
                this.saveLayout()
            }, 400)
        },
        saveLayout() {
            axios.post('api/remembertable/savecolsaves', {
                area: DASHBOARD_LAYOUT_AREA,
                fields: this.layout.map((widget) => ({
                    key: widget.key,
                    visible: parseInt(widget.visible) === 1 ? 1 : 0,
                    collapsed: parseInt(widget.collapsed) === 1 ? 1 : 0,
                })),
            }).catch(() => {
                // Keep local layout even if save fails.
            })
        },
        loadSummary() {
            this.loading = true
            this.error = ''

            const params = {}

            if (this.period === 'custom' && this.dateFrom && this.dateTo) {
                params.period = 'custom'
                params.date_from = this.dateFrom
                params.date_to = this.dateTo
            } else {
                params.period = this.period === 'custom' ? 'mtd' : this.period
            }

            if (this.canSelectSalesRep && this.salesRepId) {
                params.sales_rep_id = this.salesRepId
            }

            axios.get('/api/dashboard/summary', {
                params,
            }).then((response) => {
                if (response.data.status === 'success') {
                    const data = response.data.data || {}
                    this.viewSelfOnly = Boolean(response.data.view_self_only)
                    this.salesRepOptions = response.data.sales_reps || []

                    if (this.viewSelfOnly && response.data.sales_rep_id) {
                        this.salesRepId = response.data.sales_rep_id
                    }

                    this.summary = {
                        kpis: data.kpis || {},
                        sales: {
                            last_week: this.normalizeRanking(data.sales && data.sales.last_week),
                            last_month: this.normalizeRanking(data.sales && data.sales.last_month),
                            closed_invoices: this.normalizeRanking(data.sales && data.sales.closed_invoices),
                            unpaid_invoices: this.normalizeRanking(data.sales && data.sales.unpaid_invoices),
                            aging_ar: this.normalizeRanking(data.sales && data.sales.aging_ar),
                        },
                        charts: {
                            closed_invoices: (data.charts && data.charts.closed_invoices) || [],
                            unpaid_invoices: (data.charts && data.charts.unpaid_invoices) || [],
                            aging_ar: (data.charts && data.charts.aging_ar) || [],
                            top_products: (data.charts && data.charts.top_products) || [],
                            top_customers: (data.charts && data.charts.top_customers) || [],
                        },
                        velocity: {
                            window_days: (data.velocity && data.velocity.window_days) || 90,
                            order_growth: (data.velocity && data.velocity.order_growth) || [],
                            lapse_ratio: (data.velocity && data.velocity.lapse_ratio) || [],
                            sku_breadth: (data.velocity && data.velocity.sku_breadth) || [],
                        },
                    }
                } else {
                    this.error = response.data.message || 'Failed to load dashboard.'
                }
            }).catch(() => {
                this.error = 'Failed to load dashboard. Please try again.'
            }).finally(() => {
                this.loading = false
            })
        },
        normalizeRanking(payload) {
            return {
                total: payload && payload.total ? Number(payload.total) : 0,
                rankings: payload && payload.rankings ? payload.rankings : [],
                date_from: payload && payload.date_from ? payload.date_from : '',
                date_to: payload && payload.date_to ? payload.date_to : '',
            }
        },
        formatRankingRange(ranking, prefix) {
            if (!ranking || !ranking.date_from || !ranking.date_to) {
                return prefix || ''
            }

            const range = this.formatShortDate(ranking.date_from) + ' – ' + this.formatShortDate(ranking.date_to)
            return prefix ? prefix + ' · ' + range : range
        },
        formatShortDate(value) {
            const parts = String(value).split('-')
            if (parts.length !== 3) {
                return value
            }

            const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))
            if (Number.isNaN(date.getTime())) {
                return value
            }

            return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
        },
        buildSalesRankingLink(kind, row) {
            if (!this.canViewSalesOrders || !row) {
                return null
            }

            const query = this.buildSalesRankingQuery(kind, row)
            const resolved = this.$router.resolve({
                name: 'SalesOrders',
                query,
            })

            return resolved && resolved.href ? resolved.href : null
        },
        buildSalesRankingQuery(kind, row) {
            const query = {}

            if (row.sales_rep_id) {
                query.salesReps = String(row.sales_rep_id)
            }

            if (kind === 'last_week' || kind === 'last_month') {
                query.status = 'Closed'
                const ranking = this.summary && this.summary.sales && this.summary.sales[kind]
                if (ranking && ranking.date_from) {
                    query.from = ranking.date_from
                }
                if (ranking && ranking.date_to) {
                    query.to = ranking.date_to
                }
                return query
            }

            if (kind === 'closed') {
                query.status = 'Closed'
                Object.assign(query, this.buildDashboardDateQuery())
                return query
            }

            // Unpaid / aging: payment-eligible orders with a remaining balance.
            query.status = 'Closed,Dispatched'
            query.cf = JSON.stringify({
                balance: {
                    type: 'number',
                    from: 0.01,
                    to: '',
                    operator: 'between',
                },
            })

            if (kind === 'aging') {
                // Matches dashboard aging (30+ days). Custom Home dates still win when set.
                if (this.dateFrom || this.dateTo) {
                    Object.assign(query, this.buildDashboardDateQuery())
                } else {
                    query.period = 'OlderThan30'
                }
                return query
            }

            // Unpaid is a point-in-time AR snapshot; only pass dates when the user set them.
            if (this.dateFrom || this.dateTo) {
                Object.assign(query, this.buildDashboardDateQuery())
            }

            return query
        },
        buildDashboardDateQuery() {
            const query = {}

            if (this.dateFrom || this.dateTo) {
                if (this.dateFrom) {
                    query.from = this.dateFrom
                }
                if (this.dateTo) {
                    query.to = this.dateTo
                }
                return query
            }

            if (this.period === 'ytd') {
                query.period = 'Year To Date'
            } else if (this.period === 'mtd' || this.period === 'custom') {
                query.period = 'This Month'
            }

            return query
        },
    },
}
</script>

<style scoped>
.dashboard-page {
    padding-bottom: 2rem;
}

.dashboard-section-title {
    font-weight: 700;
}

.dashboard-range-controls {
    gap: 0.5rem;
}

.dashboard-date-picker {
    width: 9.5rem;
}

.dashboard-sales-rep-select {
    width: 12rem;
}

.dashboard-sales-rep-select ::v-deep .vue-treeselect__control {
    height: 31px;
    min-height: 31px;
}

.dashboard-sales-rep-select ::v-deep .vue-treeselect__placeholder,
.dashboard-sales-rep-select ::v-deep .vue-treeselect__single-value {
    line-height: 29px;
}

@media (min-width: 1200px) {
    .dashboard-col-fifth {
        flex: 0 0 20%;
        max-width: 20%;
    }
}
</style>
